import { FeatherChevronDown } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { Button } from "../../../../../ui/components/Button";
import { DropdownMenu } from "../../../../../ui/components/DropdownMenu";
import React from "react";
import type {
  AccountantStatusBillsDetailEntity,
  AccountantStatusInvoicesDetailEntity,
  AccountantStatusTransactionsDetailEntity,
  AccountantStatusWorkHistoryEntity,
} from "../../../domain/entities/AccountantStatusEntity";
import { BillsDetailSection } from "./BillsDetailSection";
import { InvoicesDetailSection } from "./InvoicesDetailSection";
import { TransactionsDetailSection } from "./TransactionsDetailSection";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface AccountantMonthDrilldownSectionProps {
  detailYear: number;
  detailMonth: number;
  detailPeriod: "this_month" | "last_3_months";
  workHistory: AccountantStatusWorkHistoryEntity[];
  onDetailMonthChange: (year: number, month: number) => void;
  onDetailPeriodChange: (period: "this_month" | "last_3_months") => void;
  getMonthNumberFromAbbreviation: (abbr: string) => number;
  billsDetail: AccountantStatusBillsDetailEntity | null;
  billsDetailLoading: boolean;
  billsDetailError: string | null;
  billsOffset: number;
  billsLimit: number;
  onBillsPrevious: () => void;
  onBillsNext: () => void;
  invoicesDetail: AccountantStatusInvoicesDetailEntity | null;
  invoicesDetailLoading: boolean;
  invoicesDetailError: string | null;
  invoicesOffset: number;
  invoicesLimit: number;
  onInvoicesPrevious: () => void;
  onInvoicesNext: () => void;
  transactionsDetail: AccountantStatusTransactionsDetailEntity | null;
  transactionsDetailLoading: boolean;
  transactionsDetailError: string | null;
}

export function AccountantMonthDrilldownSection({
  detailYear,
  detailMonth,
  detailPeriod,
  workHistory,
  onDetailMonthChange,
  onDetailPeriodChange,
  getMonthNumberFromAbbreviation,
  billsDetail,
  billsDetailLoading,
  billsDetailError,
  billsOffset,
  billsLimit,
  onBillsPrevious,
  onBillsNext,
  invoicesDetail,
  invoicesDetailLoading,
  invoicesDetailError,
  invoicesOffset,
  invoicesLimit,
  onInvoicesPrevious,
  onInvoicesNext,
  transactionsDetail,
  transactionsDetailLoading,
  transactionsDetailError,
}: AccountantMonthDrilldownSectionProps) {
  const detailPeriodRangeLabel =
    billsDetail?.periodRange?.label ||
    invoicesDetail?.periodRange?.label ||
    transactionsDetail?.periodRange?.label ||
    null;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full items-start justify-between gap-6">
        <div className="flex flex-col items-start gap-1">
          <span className="text-body font-body text-subtext-color">
            Drill down into the details for a specific month.
          </span>
          {detailPeriodRangeLabel && (
            <span className="text-caption font-caption text-neutral-600">
              {detailPeriodRangeLabel}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <span className="text-body-bold font-body-bold text-subtext-color">
            Viewing:
          </span>
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <Button
                variant="neutral-secondary"
                size="medium"
                iconRight={<FeatherChevronDown />}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
              >
                {detailPeriod === "last_3_months"
                  ? "Last 3 Months"
                  : `${MONTH_NAMES[detailMonth - 1]} ${detailYear}`}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="start"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => {
                      onDetailPeriodChange("last_3_months");
                    }}
                    className={
                      detailPeriod === "last_3_months" ? "bg-brand-50" : ""
                    }
                  >
                    Last 3 Months
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownDivider />
                  {workHistory.map((item, idx) => {
                    const monthNumber = getMonthNumberFromAbbreviation(
                      item.month,
                    );
                    const isSelected =
                      detailYear === item.year && detailMonth === monthNumber;
                    return (
                      <DropdownMenu.DropdownItem
                        key={`${item.year}-${monthNumber}-${idx}`}
                        icon={null}
                        onClick={() => {
                          onDetailPeriodChange("this_month");
                          onDetailMonthChange(item.year, monthNumber);
                        }}
                        className={
                          isSelected && detailPeriod === "this_month"
                            ? "bg-brand-50"
                            : ""
                        }
                      >
                        {MONTH_NAMES[monthNumber - 1]} {item.year}
                      </DropdownMenu.DropdownItem>
                    );
                  })}
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
        </div>
      </div>

      <InvoicesDetailSection
        data={invoicesDetail}
        loading={invoicesDetailLoading}
        error={invoicesDetailError}
        offset={invoicesOffset}
        limit={invoicesLimit}
        onPrevious={onInvoicesPrevious}
        onNext={onInvoicesNext}
      />
      <BillsDetailSection
        data={billsDetail}
        loading={billsDetailLoading}
        error={billsDetailError}
        offset={billsOffset}
        limit={billsLimit}
        onPrevious={onBillsPrevious}
        onNext={onBillsNext}
      />
      <TransactionsDetailSection
        data={transactionsDetail}
        loading={transactionsDetailLoading}
        error={transactionsDetailError}
      />
    </div>
  );
}
